import { useState, useEffect } from 'react';
import { X, AlertCircle, Settings } from 'lucide-react';
import api from '../lib/api';

interface EditMatchCriteriaModalProps {
  jobId: string;
  currentCriteria: {
    tags: any[];
    demographicMix: any;
    estimatedKOL: number;
    broadcastMultiplier: number;
    shortlistMultiplier: number;
  };
  onClose: () => void;
  onSave: () => void;
}

export default function EditMatchCriteriaModal({ jobId, currentCriteria, onClose, onSave }: EditMatchCriteriaModalProps) {
  const [tags, setTags] = useState<string[]>(
    currentCriteria.tags?.map(t => t.tagId || t.tag?.id || t.id) || []
  );
  const [allTags, setAllTags] = useState<any[]>([]);
  const [demographicMix, setDemographicMix] = useState(currentCriteria.demographicMix || {});
  const [estimatedKOL, setEstimatedKOL] = useState(currentCriteria.estimatedKOL || 10);
  const [broadcastMultiplier, setBroadcastMultiplier] = useState(
    currentCriteria.broadcastMultiplier ?? 3,
  );
  const [shortlistMultiplier, setShortlistMultiplier] = useState(
    currentCriteria.shortlistMultiplier ?? 1,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const response = await api.get('/tags');
      setAllTags(response.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      await api.patch(`/jobs/${jobId}`, {
        jobTags: tags,
        demographicMix: Object.keys(demographicMix).length > 0 ? demographicMix : null,
        estimatedKOL,
        broadcastMultiplier,
        shortlistMultiplier,
      });

      onSave();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update criteria');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const updateDemographic = (key: string, value: any) => {
    setDemographicMix((prev: any) => {
      if (!value || value === '') {
        const newMix = { ...prev };
        delete newMix[key];
        return newMix;
      }
      return { ...prev, [key]: value };
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-glass">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-accent2" />
            <div>
              <h2 className="text-xl font-semibold tracking-tightest text-glass-text">Edit Match Criteria</h2>
              <p className="text-sm text-glass-text-2">Adjust criteria to find more matches</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-glass-text-3 hover:text-glass-text transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto glass-scroll max-h-[calc(90vh-180px)]">
          {error && (
            <div className="mb-4 p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-rose-500 dark:text-rose-300 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rose-500 dark:text-rose-300">{error}</p>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 p-4 rounded-xl border border-accent2/30 bg-accent2/10">
            <p className="text-sm text-accent2">
              <strong>Tip:</strong> If you're getting too few matches, try:
            </p>
            <ul className="text-sm text-glass-text-2 mt-2 ml-4 space-y-1">
              <li>• Reducing the number of tags</li>
              <li>• Removing demographic filters (race, age, sex)</li>
              <li>• Increasing target KOL count</li>
            </ul>
          </div>

          <div className="space-y-6">
            {/* Tags Section */}
            <div>
              <label className="block text-sm font-medium text-glass-text mb-3">
                Content tags (optional — leave empty to match the whole roster using demographics only)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      tags.includes(tag.id)
                        ? 'bg-accent2/10 border-accent2 text-accent2'
                        : 'bg-glass-2 border-glass text-glass-text-2 hover:border-glass-strong'
                    }`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-glass-text-3">
                Selected: {tags.length} tag{tags.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Target KOL Count */}
            <div>
              <label className="block text-sm font-medium text-glass-text mb-2">
                Target KOL Count
              </label>
              <input
                type="number"
                value={estimatedKOL}
                onChange={(e) => setEstimatedKOL(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                max="100"
                className="w-full px-4 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
              />
              <p className="mt-1 text-xs text-glass-text-3">
                Matching preview uses your tag and demographic filters; target count drives broadcast/shortlist
                sizing below.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-glass-text mb-2">
                  Broadcast multiplier
                </label>
                <input
                  type="number"
                  value={broadcastMultiplier}
                  onChange={(e) =>
                    setBroadcastMultiplier(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  min={1}
                  max={20}
                  className="w-full px-4 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                />
                <p className="mt-1 text-xs text-glass-text-3">
                  Will send invitations to up to {estimatedKOL * broadcastMultiplier} KOLs (
                  {estimatedKOL} × {broadcastMultiplier})
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-glass-text mb-2">
                  Shortlist multiplier
                </label>
                <input
                  type="number"
                  value={shortlistMultiplier}
                  onChange={(e) =>
                    setShortlistMultiplier(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  min={1}
                  max={20}
                  className="w-full px-4 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                />
                <p className="mt-1 text-xs text-glass-text-3">
                  Shortlist up to {estimatedKOL * shortlistMultiplier} accepted KOLs ({estimatedKOL} ×{' '}
                  {shortlistMultiplier})
                </p>
                <p className="mt-1 text-xs text-glass-text-3">
                  Production shortlist target remains {estimatedKOL} KOLs.
                </p>
              </div>
            </div>
            {estimatedKOL * shortlistMultiplier > estimatedKOL * broadcastMultiplier && (
              <div className="p-3 rounded-xl border border-amber-400/30 bg-amber-400/10 text-xs text-amber-500 dark:text-amber-300">
                Accepted review pool ({estimatedKOL * shortlistMultiplier}) is larger than broadcast target (
                {estimatedKOL * broadcastMultiplier}). Consider lowering the shortlist multiplier or raising
                broadcast/target KOL count.
              </div>
            )}

            {/* Demographics Section */}
            <div className="border-t border-glass pt-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-glass-text">
                  Demographic Filters (Optional)
                </label>
                <button
                  onClick={() => setDemographicMix({})}
                  className="text-xs text-accent2 hover:underline font-medium"
                >
                  Clear All
                </button>
              </div>

              <div className="space-y-4">
                {/* Majority Race */}
                <div>
                  <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                    Majority Race (≥40%)
                  </label>
                  <select
                    value={demographicMix.majorityRace || ''}
                    onChange={(e) => updateDemographic('majorityRace', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  >
                    <option value="">No requirement</option>
                    <option value="malay">Malay</option>
                    <option value="chinese">Chinese</option>
                    <option value="indian">Indian</option>
                  </select>
                </div>

                {/* Sex */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                      Gender
                    </label>
                    <select
                      value={demographicMix.sex || ''}
                      onChange={(e) => updateDemographic('sex', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                    >
                      <option value="">No requirement</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                      Threshold (%)
                    </label>
                    <input
                      type="number"
                      value={demographicMix.sexThreshold || 50}
                      onChange={(e) => updateDemographic('sexThreshold', parseInt(e.target.value))}
                      min="50"
                      max="90"
                      step="5"
                      disabled={!demographicMix.sex}
                      className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40 disabled:opacity-50"
                    />
                  </div>
                </div>

                {/* Age Group */}
                <div>
                  <label className="block text-xs font-medium text-glass-text-2 mb-1.5">
                    Age Group (≥30%)
                  </label>
                  <select
                    value={demographicMix.ageGroup || ''}
                    onChange={(e) => updateDemographic('ageGroup', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-glass bg-glass-2 text-glass-text text-sm focus:outline-none focus:ring-2 focus:ring-accent2/40"
                  >
                    <option value="">No requirement</option>
                    <option value="age18_24">18-24 years</option>
                    <option value="age25_34">25-34 years</option>
                    <option value="age35_plus">35+ years</option>
                  </select>
                </div>
              </div>

              <div className="mt-3 p-3 rounded-xl border border-amber-400/30 bg-amber-400/10">
                <p className="text-xs text-amber-500 dark:text-amber-300">
                  <strong>Note:</strong> Demographic filters are strict requirements. Remove them if you're getting no matches.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-glass bg-glass-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-glass-text-2 hover:bg-glass-3 rounded-lg transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-accent px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Saving...</span>
              </>
            ) : (
              <span>Save & Re-Match</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

